import React, { useState, useEffect } from 'react';
import "../../css/Grades.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaGraduationCap, FaSearch, FaTimes, FaFilter, FaDownload, FaTable, FaChartBar, FaPrint } from 'react-icons/fa';
import Loader from "../../components/Loader";

function Grades() {
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [examResults, setExamResults] = useState([]);
  const [examStats, setExamStats] = useState(null);
  const [examDetails, setExamDetails] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const [sortConfig, setSortConfig] = useState({ key: 'studentName', direction: 'asc' });
  const [sections, setSections] = useState([]);
  const [showExamInfoModal, setShowExamInfoModal] = useState(false);

  // Fetch classes
  const fetchClasses = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/classes", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch classes");
      }

      const data = await response.json();
      setClasses(data);
    } catch (error) {
      console.error("Error fetching classes:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // Fetch subjects
  const fetchSubjects = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/subjects", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch subjects");
      }

      const data = await response.json();
      setSubjects(data);
    } catch (error) {
      console.error("Error fetching subjects:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // Fetch exams
  const fetchExams = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/exams", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch exams");
      }

      const data = await response.json();
      // Only show exams with results uploaded
      const examsWithResults = data.filter(exam => exam.resultsUploaded);
      setExams(examsWithResults);
    } catch (error) {
      console.error("Error fetching exams:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch exam results
  const fetchExamResults = async (examId) => {
    if (!examId) return;
    
    setIsLoading(true);
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`https://schoolpro-api.uniums.com/api/exams/${examId}/results`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch exam results");
      }

      const data = await response.json();
      setExamResults(data.results);
      setExamStats(data.stats);
      setExamDetails(data.exam);
    } catch (error) {
      console.error("Error fetching exam results:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle class change
  const handleClassChange = (e) => {
    const classId = e.target.value;
    setSelectedClass(classId);
    setSelectedSection('');

    // Find the selected class and get its sections
    const selectedClassObj = classes.find(c => c._id === classId);
    if (selectedClassObj && selectedClassObj.section) {
      // Split the comma-separated string into an array
      const sectionsArray = selectedClassObj.section.split(',').map(s => s.trim());
      setSections(['all', ...sectionsArray]);
    } else {
      setSections([]);
    }
  };

  // Filter results based on search term and filters
  const getFilteredResults = () => {
    if (!examResults.length) return [];

    let filtered = [...examResults];

    // Apply section filter if selected and not 'all'
    if (selectedSection && selectedSection !== 'all') {
      // This assumes student data has a section field or it's part of the studentId
      // Adapt based on your actual data structure
      filtered = filtered.filter(result => {
        // This is an example - adjust according to your data structure
        return result.studentId.includes(selectedSection) || 
               (result.section && result.section === selectedSection);
      });
    }

    // Apply search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(result => 
        result.studentName.toLowerCase().includes(term) || 
        result.studentId.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  // Sort the results
  const sortedResults = () => {
    const filtered = getFilteredResults();
    
    if (!sortConfig.key) return filtered;

    return [...filtered].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Request sort
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort indicator
  const getSortDirectionIndicator = (column) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get class name
  const getClassName = (classId) => {
    const classItem = classes.find(c => c._id === classId);
    return classItem ? classItem.name : 'Unknown Class';
  };

  // Get subject name
  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s._id === subjectId);
    return subject ? subject.name : 'Unknown Subject';
  };

  // Get grade letter based on percentage
  const getGradeLetter = (marks, totalMarks) => {
    const percentage = (marks / totalMarks) * 100;
    
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C+';
    if (percentage >= 40) return 'C';
    if (percentage >= 33) return 'D';
    return 'F';
  };

  // Get color based on grade
  const getGradeColor = (grade) => {
    switch(grade) {
      case 'A+': return 'grade-color-a-plus';
      case 'A': return 'grade-color-a';
      case 'B+': return 'grade-color-b-plus';
      case 'B': return 'grade-color-b';
      case 'C+': return 'grade-color-c-plus';
      case 'C': return 'grade-color-c';
      case 'D': return 'grade-color-d';
      case 'F': return 'grade-color-f';
      default: return '';
    }
  };

  // Export results as CSV
  const exportResultsAsCSV = () => {
    if (!examResults.length || !examDetails) return;

    const results = sortedResults();
    const examName = examDetails.examName;
    const className = getClassName(examDetails.class);
    const subjectName = getSubjectName(examDetails.subject);
    
    // Create CSV header
    let csvContent = "Student ID,Student Name,Marks,Percentage,Grade,Result\n";
    
    // Add data rows
    results.forEach(result => {
      const percentage = ((result.marks / examDetails.totalMarks) * 100).toFixed(2);
      const grade = getGradeLetter(result.marks, examDetails.totalMarks);
      const resultStatus = result.isPassed ? 'Pass' : 'Fail';
      
      csvContent += `${result.studentId},${result.studentName},${result.marks},${percentage}%,${grade},${resultStatus}\n`;
    });
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${examName}_${className}_${subjectName}_results.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print results
  const printResults = () => {
    window.print();
  };

  // Effect hooks
  useEffect(() => {
    fetchClasses();
    fetchSubjects();
    fetchExams();
  }, []);

  useEffect(() => {
    if (selectedExam) {
      fetchExamResults(selectedExam);
    } else {
      setExamResults([]);
      setExamStats(null);
      setExamDetails(null);
    }
  }, [selectedExam]);

  // Clear all filters
  const clearFilters = () => {
    setSelectedExam('');
    setSelectedClass('');
    setSelectedSection('');
    setSelectedSubject('');
    setSearchTerm('');
    setExamResults([]);
    setExamStats(null);
    setExamDetails(null);
  };

  return (
    <div className="grade-container">
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
      
      <div className="grade-header">
        <div className="grade-title-container">
          <FaGraduationCap className="grade-title-icon" />
          <h1 className="grade-title">Student Grades & Results</h1>
        </div>

        {examStats && (
          <div className="grade-stats">
            <div className="grade-stat-box">
              <div className="grade-stat-icon grade-stat-icon-total">
                <FaGraduationCap />
              </div>
              <div className="grade-stat-info">
                <span className="grade-stat-number">{examStats.totalStudents}</span>
                <span className="grade-stat-label">Total Students</span>
              </div>
            </div>
            <div className="grade-stat-box">
              <div className="grade-stat-icon grade-stat-icon-passed">
                <FaGraduationCap />
              </div>
              <div className="grade-stat-info">
                <span className="grade-stat-number">{examStats.passedStudents}</span>
                <span className="grade-stat-label">Passed Students</span>
              </div>
            </div>
            <div className="grade-stat-box">
              <div className="grade-stat-icon grade-stat-icon-failed">
                <FaGraduationCap />
              </div>
              <div className="grade-stat-info">
                <span className="grade-stat-number">{examStats.failedStudents}</span>
                <span className="grade-stat-label">Failed Students</span>
              </div>
            </div>
            <div className="grade-stat-box">
              <div className="grade-stat-icon grade-stat-icon-average">
                <FaChartBar />
              </div>
              <div className="grade-stat-info">
                <span className="grade-stat-number">{examStats.averageMarks}</span>
                <span className="grade-stat-label">Average Marks</span>
              </div>
            </div>
          </div>
        )}

        <div className="grade-actions">
          <button 
            className={`grade-view-btn ${viewMode === 'table' ? 'grade-active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            <FaTable /> Table View
          </button>
          <button 
            className={`grade-view-btn ${viewMode === 'card' ? 'grade-active' : ''}`}
            onClick={() => setViewMode('card')}
          >
            <FaGraduationCap /> Card View
          </button>
          {examResults.length > 0 && (
            <>
              <button className="grade-btn grade-btn-export" onClick={exportResultsAsCSV}>
                <FaDownload /> Export CSV
              </button>
              <button className="grade-btn grade-btn-print" onClick={printResults}>
                <FaPrint /> Print
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grade-filters">
        <div className="grade-filter-row">
          <div className="grade-filter-group">
            <label className="grade-filter-label">Select Exam</label>
            <select 
              className="grade-filter-select"
              value={selectedExam}
              onChange={(e) => setSelectedExam(e.target.value)}
            >
              <option value="">All Exams</option>
              {exams.map(exam => (
                <option key={exam._id} value={exam._id}>
                  {exam.examName} - {getSubjectName(exam.subject)} ({getClassName(exam.class)})
                </option>
              ))}
            </select>
          </div>
          
          <div className="grade-filter-group">
            <label className="grade-filter-label">Class</label>
            <select 
              className="grade-filter-select"
              value={selectedClass}
              onChange={handleClassChange}
              disabled={!selectedExam}
            >
              <option value="">All Classes</option>
              {classes.map(classItem => (
                <option key={classItem._id} value={classItem._id}>
                  {classItem.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="grade-filter-group">
            <label className="grade-filter-label">Section</label>
            <select 
              className="grade-filter-select"
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              disabled={!selectedClass || !sections.length}
            >
              <option value="">All Sections</option>
              {sections.map((section, index) => (
                <option key={index} value={section}>
                  {section === 'all' ? 'All Sections' : section}
                </option>
              ))}
            </select>
          </div>
          
          <div className="grade-filter-group">
            <label className="grade-filter-label">Subject</label>
            <select 
              className="grade-filter-select"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              disabled={!selectedExam}
            >
              <option value="">All Subjects</option>
              {subjects.map(subject => (
                <option key={subject._id} value={subject._id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="grade-search-container">
          <FaSearch className="grade-search-icon" />
          <input
            type="text"
            className="grade-search-input"
            placeholder="Search by student name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="grade-search-clear" onClick={() => setSearchTerm('')}>
              <FaTimes />
            </button>
          )}
          <button className="grade-btn grade-btn-clear-filters" onClick={clearFilters}>
            <FaTimes /> Clear Filters
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="grade-loading">
          <Loader />
          <p>Loading results...</p>
        </div>
      ) : (
        <div className="grade-content">
          {selectedExam && examDetails ? (
            <div className="grade-exam-info">
              <h2 className="grade-exam-title">{examDetails.examName}</h2>
              <div className="grade-exam-details">
                <span>Class: {getClassName(examDetails.class)}</span>
                <span>Subject: {getSubjectName(examDetails.subject)}</span>
                <span>Date: {formatDate(examDetails.date)}</span>
                <span>Total Marks: {examDetails.totalMarks}</span>
                <span>Passing Marks: {examDetails.passingMarks}</span>
              </div>
              <button 
                className="grade-btn grade-btn-info"
                onClick={() => setShowExamInfoModal(true)}
              >
                View Exam Details
              </button>
            </div>
          ) : (
            <div className="grade-select-prompt">
              <FaGraduationCap className="grade-prompt-icon" />
              <h2>Select an Exam to View Results</h2>
              <p>Choose an exam from the dropdown above to view student grades and performance statistics.</p>
            </div>
          )}

          {examResults.length > 0 ? (
            viewMode === 'table' ? (
              <div className="grade-table-container">
                <table className="grade-table">
                  <thead>
                    <tr>
                      <th onClick={() => requestSort('studentId')}>
                        Student ID {getSortDirectionIndicator('studentId')}
                      </th>
                      <th onClick={() => requestSort('studentName')}>
                        Student Name {getSortDirectionIndicator('studentName')}
                      </th>
                      <th onClick={() => requestSort('marks')}>
                        Marks {getSortDirectionIndicator('marks')}
                      </th>
                      <th>Percentage</th>
                      <th>Grade</th>
                      <th onClick={() => requestSort('isPassed')}>
                        Result {getSortDirectionIndicator('isPassed')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedResults().map((result, index) => {
                      const percentage = ((result.marks / examDetails.totalMarks) * 100).toFixed(2);
                      const grade = getGradeLetter(result.marks, examDetails.totalMarks);
                      const gradeColorClass = getGradeColor(grade);
                      
                      return (
                        <tr key={index} className={result.isPassed ? 'grade-row-pass' : 'grade-row-fail'}>
                          <td>{result.studentId}</td>
                          <td>{result.studentName}</td>
                          <td>{result.marks} / {examDetails.totalMarks}</td>
                          <td>{percentage}%</td>
                          <td className={gradeColorClass}>{grade}</td>
                          <td>
                            <span className={`grade-result-badge ${result.isPassed ? 'grade-result-pass' : 'grade-result-fail'}`}>
                              {result.isPassed ? 'Pass' : 'Fail'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grade-card-grid">
                {sortedResults().map((result, index) => {
                  const percentage = ((result.marks / examDetails.totalMarks) * 100).toFixed(2);
                  const grade = getGradeLetter(result.marks, examDetails.totalMarks);
                  const gradeColorClass = getGradeColor(grade);
                  
                  return (
                    <div key={index} className={`grade-result-card ${result.isPassed ? 'grade-card-pass' : 'grade-card-fail'}`}>
                      <div className="grade-card-student-info">
                        <h3 className="grade-card-student-name">{result.studentName}</h3>
                        <p className="grade-card-student-id">ID: {result.studentId}</p>
                      </div>
                      <div className="grade-card-result">
                        <div className={`grade-card-grade ${gradeColorClass}`}>
                          {grade}
                        </div>
                        <div className="grade-card-marks">
                          <span className="grade-card-marks-value">{result.marks}</span>
                          <span className="grade-card-marks-total">/{examDetails.totalMarks}</span>
                        </div>
                      </div>
                      <div className="grade-card-details">
                        <div className="grade-card-detail">
                          <span className="grade-card-detail-label">Percentage:</span>
                          <span className="grade-card-detail-value">{percentage}%</span>
                        </div>
                        <div className="grade-card-detail">
                          <span className="grade-card-detail-label">Result:</span>
                          <span className={`grade-card-detail-value ${result.isPassed ? 'grade-text-pass' : 'grade-text-fail'}`}>
                            {result.isPassed ? 'Pass' : 'Fail'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : selectedExam ? (
            <div className="grade-no-results">
              <h3>No Results Found</h3>
              <p>There are no results matching your current filters.</p>
            </div>
          ) : null}
        </div>
      )}

      {/* Exam Details Modal */}
      {showExamInfoModal && examDetails && (
        <div className="grade-modal-overlay">
          <div className="grade-modal">
            <div className="grade-modal-header">
              <h2 className="grade-modal-title">Exam Details</h2>
              <button className="grade-modal-close" onClick={() => setShowExamInfoModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="grade-modal-content">
              <div className="grade-exam-detail-grid">
                <div className="grade-exam-detail-item">
                  <span className="grade-exam-detail-label">Exam Name:</span>
                  <span className="grade-exam-detail-value">{examDetails.examName}</span>
                </div>
                <div className="grade-exam-detail-item">
                  <span className="grade-exam-detail-label">Subject:</span>
                  <span className="grade-exam-detail-value">{getSubjectName(examDetails.subject)}</span>
                </div>
                <div className="grade-exam-detail-item">
                  <span className="grade-exam-detail-label">Class:</span>
                  <span className="grade-exam-detail-value">{getClassName(examDetails.class)}</span>
                </div>
                <div className="grade-exam-detail-item">
                  <span className="grade-exam-detail-label">Section:</span>
                  <span className="grade-exam-detail-value">{examDetails.section}</span>
                </div>
                <div className="grade-exam-detail-item">
                  <span className="grade-exam-detail-label">Date:</span>
                  <span className="grade-exam-detail-value">{formatDate(examDetails.date)}</span>
                </div>
                <div className="grade-exam-detail-item">
                  <span className="grade-exam-detail-label">Time:</span>
                  <span className="grade-exam-detail-value">{examDetails.startTime} - {examDetails.endTime}</span>
                </div>
                <div className="grade-exam-detail-item">
                  <span className="grade-exam-detail-label">Total Marks:</span>
                  <span className="grade-exam-detail-value">{examDetails.totalMarks}</span>
                </div>
                <div className="grade-exam-detail-item">
                  <span className="grade-exam-detail-label">Passing Marks:</span>
                  <span className="grade-exam-detail-value">{examDetails.passingMarks}</span>
                </div>
                <div className="grade-exam-detail-item">
                  <span className="grade-exam-detail-label">Exam Type:</span>
                  <span className="grade-exam-detail-value">
                    {examDetails.examType.charAt(0).toUpperCase() + examDetails.examType.slice(1)}
                  </span>
                </div>
              </div>

              {examDetails.description && (
                <div className="grade-exam-description">
                  <h3 className="grade-exam-description-title">Description:</h3>
                  <p className="grade-exam-description-text">{examDetails.description}</p>
                </div>
              )}

              {examStats && (
                <div className="grade-exam-stats-detail">
                  <h3 className="grade-exam-stats-title">Performance Statistics</h3>
                  <div className="grade-exam-stats-grid">
                    <div className="grade-exam-stat-item">
                      <span className="grade-exam-stat-label">Total Students:</span>
                      <span className="grade-exam-stat-value">{examStats.totalStudents}</span>
                    </div>
                    <div className="grade-exam-stat-item">
                      <span className="grade-exam-stat-label">Passed Students:</span>
                      <span className="grade-exam-stat-value grade-text-pass">{examStats.passedStudents}</span>
                    </div>
                    <div className="grade-exam-stat-item">
                      <span className="grade-exam-stat-label">Failed Students:</span>
                      <span className="grade-exam-stat-value grade-text-fail">{examStats.failedStudents}</span>
                    </div>
                    <div className="grade-exam-stat-item">
                      <span className="grade-exam-stat-label">Pass Percentage:</span>
                      <span className="grade-exam-stat-value">
                        {examStats.totalStudents ? ((examStats.passedStudents / examStats.totalStudents) * 100).toFixed(2) : 0}%
                      </span>
                    </div>
                    <div className="grade-exam-stat-item">
                      <span className="grade-exam-stat-label">Highest Marks:</span>
                      <span className="grade-exam-stat-value grade-text-high">{examStats.highestMarks}</span>
                    </div>
                    <div className="grade-exam-stat-item">
                      <span className="grade-exam-stat-label">Lowest Marks:</span>
                      <span className="grade-exam-stat-value">{examStats.lowestMarks}</span>
                    </div>
                    <div className="grade-exam-stat-item">
                      <span className="grade-exam-stat-label">Average Marks:</span>
                      <span className="grade-exam-stat-value">{examStats.averageMarks}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="grade-modal-footer">
              <button className="grade-btn grade-btn-primary" onClick={() => setShowExamInfoModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Grades;